import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Checkbox, Alert, Modal } from "antd";
import { useAuth } from "../../context/AuthContext";
import { API_BASE_URL, API_ENDPOINTS } from "../../config/api";
import axios from "axios";
import LogoMainImage from "../../assets/logo/splended_logo.png";
import Toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const [form] = Form.useForm(); // Add form instance

  // OTP States
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [userId, setUserId] = useState("");
  const [otpTimer, setOtpTimer] = useState(300);
  const [resendLoading, setResendLoading] = useState(false);
  const [canResend, setCanResend] = useState(false);

  const {
    login,
    isAuthenticated,
    loading,
    authLoading,
    rememberMe,
    getSavedCredentials,
    clearSavedCredentials,
    updateRememberMe,
  } = useAuth();

  const navigate = useNavigate();
  const redirectAttempted = useRef(false);
  const otpInputs = useRef([]);

  // Load saved credentials - FIXED for auto-fill
  useEffect(() => {
    if (isInitialized) return;

    // Directly check localStorage for credentials
    const savedCredentials = localStorage.getItem("adminCredentials");
    if (savedCredentials) {
      try {
        const creds = JSON.parse(savedCredentials);
        console.log("Loaded saved credentials for:", creds.email);

        // Set state
        setEmail(creds.email || "");
        setPassword(creds.password || "");

        // Also set form fields directly
        form.setFieldsValue({
          email: creds.email || "",
          password: creds.password || "",
        });
      } catch (e) {
        console.error("Error parsing saved credentials:", e);
      }
    }

    setIsInitialized(true);
  }, [form, isInitialized]);

  // Handle redirect after successful login
  useEffect(() => {
    if (isAuthenticated && !redirectAttempted.current) {
      redirectAttempted.current = true;
      Toast.success(t("login.loginSuccess"));
      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 500);
    }
  }, [isAuthenticated, navigate]);

  // OTP Timer
  useEffect(() => {
    let interval;
    if (showOTPModal && otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [showOTPModal, otpTimer]);

  const handleSubmit = async () => {
    setError("");

    if (!email || !password) {
      setError(t("login.pleaseEnterBothFields"));
      return;
    }

    if (authLoading) return;

    const result = await login(email, password, rememberMe);

    console.log("Login Result:", result);

    if (result?.status === true && result?.user_id) {
      setUserId(result.user_id);
      setShowOTPModal(true);
      setOtpTimer(300);
      setCanResend(false);
      setOtp(["", "", "", "", "", ""]);
      Toast.success(t("login.otpSentSuccess"));
    } else if (result?.success) {
      // Direct login success
    } else if (!result?.success) {
      setError(result?.error || t("login.invalidCredentials"));
      Toast.error(result?.error || t("login.invalidCredentials"));
    }
  };

  // FIXED: Remember Me Handler - Saves to localStorage
  const handleRememberMeChange = (e) => {
    const isChecked = e.target.checked;
    console.log("Remember me changed to:", isChecked);

    // Update context
    updateRememberMe(isChecked);

    if (isChecked) {
      // Save credentials to localStorage
      if (email && password) {
        const credentials = { email, password };
        localStorage.setItem("adminCredentials", JSON.stringify(credentials));
        localStorage.setItem("rememberMe", "true");
        // Toast.success("Remember me enabled - Credentials saved");
        console.log("Credentials saved to localStorage");
      }
    } else {
      // Clear credentials from localStorage
      localStorage.removeItem("adminCredentials");
      localStorage.removeItem("rememberMe");
      clearSavedCredentials();
      // Toast.info("Remember me disabled - Credentials cleared");
      console.log("Credentials cleared from localStorage");
    }
  };

  // Update form when email/password change
  useEffect(() => {
    form.setFieldsValue({ email, password });
  }, [email, password, form]);

  // OTP Change Handler
  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      const pastedOtp = value.slice(0, 6).split("");
      const newOtp = [...otp];
      pastedOtp.forEach((digit, i) => {
        if (i < 6) newOtp[i] = digit;
      });
      setOtp(newOtp);
      if (otpInputs.current[5]) otpInputs.current[5].focus();
    } else {
      if (isNaN(value)) return;
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value !== "" && index < 5) {
        otpInputs.current[index + 1]?.focus();
      }
    }
  };

  // OTP Key Down Handler
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };

  // Verify OTP Handler
  const handleVerifyOTP = async () => {
    const otpString = otp.join("");

    if (otpString.length !== 6) {
      setOtpError(t("login.enterCompleteOtp"));
      return;
    }

    setOtpLoading(true);
    setOtpError("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.VERFIY_ADMIN_OTP}`,
        {
          user_id: userId,
          otp: otpString,
        },
      );

      console.log("Verify OTP Response:", response.data);

      if (response.data) {
        const data = response.data?.data ?? response.data;
        const tokens = data?.tokens ?? data;
        const user = data?.user ?? data?.user_id;
        const accessToken =
          tokens?.access ?? tokens?.access_token ?? tokens?.accessToken;

        if (!accessToken) {
          setOtpError(t("login.invalidServerResponse"));
          Toast.error(t("login.invalidServerResponse"));
          return;
        }

        if (rememberMe) {
          localStorage.setItem("token", accessToken);
          localStorage.setItem("adminUser", JSON.stringify(user || {}));
        } else {
          sessionStorage.setItem("token", accessToken);
          sessionStorage.setItem("adminUser", JSON.stringify(user || {}));
        }

        setShowOTPModal(false);
        Toast.success(t("login.otpVerifiedSuccess"));

        setTimeout(() => {
          window.location.href = "/dashboard";
        }, 500);
      } else {
        setOtpError(response.data?.message || t("login.invalidOtp"));
        Toast.error(response.data?.message || t("login.invalidOtp"));
      }
    } catch (error) {
      console.error("Verify OTP Error:", error);
      setOtpError(error.response?.data?.message || t("login.invalidOtp"));
      Toast.error(error.response?.data?.message || t("login.invalidOtp"));
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend OTP Handler
  const handleResendOTP = async () => {
    if (!canResend) return;

    setResendLoading(true);
    setOtpError("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.RESEND_OTP}`,
        { email },
      );

      console.log("Resend OTP Response:", response.data);

      if (response.data?.status === true) {
        setOtp(["", "", "", "", "", ""]);
        setOtpTimer(300);
        setCanResend(false);
        setOtpError("");
        setTimeout(() => otpInputs.current[0]?.focus(), 100);
        Toast.success(t("login.newOtpSent"));
      } else {
        const errorMsg = response.data?.message || t("login.failedResendOtp");
        setOtpError(errorMsg);
        Toast.error(errorMsg);
      }
    } catch (error) {
      console.error("Resend OTP Error:", error);
      const errorMsg =
        error.response?.data?.message || t("login.failedResendOtp");
      setOtpError(errorMsg);
      Toast.error(errorMsg);
    } finally {
      setResendLoading(false);
    }
  };

  // Close Modal Handler
  const handleCloseModal = () => {
    setShowOTPModal(false);
    setOtp(["", "", "", "", "", ""]);
    setOtpError("");
    setOtpTimer(300);
    setCanResend(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  if (loading) {
    return (
      <div className="login-screen">
        <div className="loading-overlay">
          <div className="spinner-large"></div>
          <p>{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="brand-section">
          <div className="brand-content">
            <div className="brand-logo">
              <div className="logo-circle">
                <img src={LogoMainImage} alt="Logo" />
              </div>
            </div>
            <h1 className="brand-title">{t("login.adminPanel")}</h1>
          </div>
        </div>

        <div className="form-section">
          <div className="form-container">
            <h2 className="login-title">{t("login.signIn")}</h2>

            {error && (
              <Alert
                type="error"
                message={error}
                showIcon
                style={{ marginBottom: 20 }}
              />
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className="login-form"
            >
              <Form.Item
                label={t("login.emailAddress")}
                name="email"
                rules={[
                  { required: true, message: t("login.pleaseEnterEmail") },
                  { type: "email", message: t("login.pleaseEnterValidEmail") },
                ]}
              >
                <Input
                  placeholder={t("login.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={authLoading}
                  size="large"
                />
              </Form.Item>

              <Form.Item
                label={t("login.passwordLabel")}
                name="password"
                rules={[
                  { required: true, message: t("login.pleaseEnterPassword") },
                  { min: 6, message: t("login.passwordMinLength") },
                ]}
              >
                <Input.Password
                  placeholder={t("login.enterPassword")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={authLoading}
                  size="large"
                />
              </Form.Item>

              <div className="remamber-and-forgot-password">
                <Form.Item name="remember" valuePropName="checked" noStyle>
                  <Checkbox
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    disabled={authLoading}
                  >
                    {t("login.rememberMe")}
                  </Checkbox>
                </Form.Item>
                <Button
                  type="link"
                  onClick={() => !authLoading && navigate("/forgot-password")}
                  disabled={authLoading}
                  style={{ padding: 0 }}
                  className="forget-password"
                >
                  {t("login.forgotPassword")}
                </Button>
              </div>

              <Form.Item style={{ marginTop: 20, marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={authLoading}
                  block
                  size="large"
                >
                  {t("login.signIn")}
                </Button>
              </Form.Item>
            </Form>

            <div className="login-footer">
              <p className="copyright-text">{t("login.copyright")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* OTP Modal */}
      <Modal
        title={t("login.verifyIdentity")}
        open={showOTPModal}
        onCancel={handleCloseModal}
        footer={null}
        closable
      >
        <p style={{ marginBottom: 16, color: "#666", textAlign: "center" }}>
          {t("login.enterOtpMessage")} <strong>{email}</strong>
        </p>
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "center",
            marginBottom: 16,
          }}
        >
          {otp.map((digit, index) => (
            <Input
              key={index}
              ref={(el) => (otpInputs.current[index] = el)}
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(index, e)}
              status={otpError ? "error" : ""}
              disabled={otpLoading || resendLoading}
              style={{
                textAlign: "center",
                fontSize: 18,
                width: 45,
                height: 45,
              }}
            />
          ))}
        </div>
        {otpError && (
          <Alert
            type="error"
            message={otpError}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <div style={{ textAlign: "center", marginBottom: 16, color: "#666" }}>
          {t("login.timeRemaining")}{" "}
          <strong style={{ color: otpTimer < 60 ? "#ff4d4f" : undefined }}>
            {formatTime(otpTimer)}
          </strong>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Button
            type="primary"
            onClick={handleVerifyOTP}
            loading={otpLoading}
            disabled={otp.join("").length !== 6 || otpLoading}
            block
            size="large"
          >
            {t("login.verifyAndSignIn")}
          </Button>
          <Button
            onClick={handleResendOTP}
            loading={resendLoading}
            disabled={!canResend || resendLoading}
            block
            size="large"
          >
            {t("login.resendCode")}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Login;
